import { useEffect, useState } from 'react'
import { useNavigate, useParams } from '@tanstack/react-router'
import { useTranslation } from '../i18n/hooks/useTranslation'
import { Category } from '../services/adminService'
import { adminService } from '../services/adminService'
import { Button } from '../components/ui/Button'
import { CategoryForm } from '../components/CategoryForm'
import { LoadingScreen } from '../components/LoadingScreen'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { toast } from 'react-hot-toast'

export function CategoryEditPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const params = useParams({ strict: false })
  const categoryId = (params as any).categoryId
  const isCreateMode = !categoryId || categoryId === undefined
  
  // Debug logging
  console.log('CategoryEditPage - categoryId:', categoryId)
  console.log('CategoryEditPage - isCreateMode:', isCreateMode)
  const [category, setCategory] = useState<Category | null>(null)
  const [loading, setLoading] = useState(true)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [deleting, setDeleting] = useState(false)

  useEffect(() => {
    loadCategory()
  }, [categoryId])

  const loadCategory = async () => {
    if (isCreateMode) {
      setLoading(false)
      return
    }

    if (isCreateMode) {
      return
    }

    try {
      setLoading(true)
      // Get the specific category by ID
      const foundCategory = await adminService.getCategory(categoryId)
      
      if (!foundCategory) {
        toast.error(t('messages.categoryNotFound'))
        navigate({ to: '/categories' })
        return
      }
      setCategory(foundCategory)
    } catch (error) {
      console.error('Error loading category:', error)
      toast.error(t('messages.categoryLoadFailed'))
      navigate({ to: '/categories' })
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (data: Omit<Category, 'id' | 'metadata'>) => {
    try {
      if (isCreateMode) {
        await adminService.createCategory(data)
        toast.success(t('messages.categoryCreatedSuccess'))
      } else {
        if (!category) return
        await adminService.updateCategory(category.id, data)
        toast.success(t('messages.categoryUpdatedSuccess'))
      }
      navigate({ to: '/categories' })
    } catch (error) {
      console.error('Error saving category:', error)
      toast.error(isCreateMode ? t('messages.categoryCreateFailed') : t('messages.categoryUpdateFailed'))
    }
  }

  const handleDelete = async () => {
    if (!category) return

    try {
      setDeleting(true)
      await adminService.deleteCategory(category.id)
      toast.success(t('messages.categoryDeletedSuccess'))
      navigate({ to: '/categories' })
    } catch (error) {
      console.error('Error deleting category:', error)
      toast.error(t('messages.categoryDeleteFailed'))
    } finally {
      setDeleting(false)
      setShowDeleteDialog(false)
    }
  }

  if (!isCreateMode && loading) {
    return <LoadingScreen />
  }

  if (!isCreateMode && !category) {
    return null
  }

  return (
    <div className="p-8">
      {/* Enhanced Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          {/* Left side - Back button and title */}
          <div className="flex flex-col items-start gap-2">
            <button
              onClick={() => navigate({ to: '/categories' })}
              className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-900 border border-gray-200 bg-gray-50 rounded-lg transition-all duration-200 group"
              aria-label={t('buttons.back')}
            >
              <svg 
                className="w-5 h-5 transition-transform group-hover:-translate-x-1" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <span className="font-medium">{t('buttons.back')}</span>
            </button>
            
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 leading-tight">
                {isCreateMode ? t('categories.createCategory') : t('categories.editCategory')}
              </h1>
              <p className="text-gray-600 mt-2 text-sm lg:text-base leading-relaxed">
                {isCreateMode 
                  ? t('categories.createSubtitle', { defaultValue: 'Create a new challenge category' })
                  : t('categories.editSubtitle', { defaultValue: 'Manage category details and translations' })
                }
              </p>
            </div>
          </div>

          {/* Right side - Action buttons */}
          {!isCreateMode && (
            <button
              onClick={() => setShowDeleteDialog(true)}
              disabled={deleting}
              className="flex items-center justify-center gap-2 px-4 py-2.5 text-red-700 bg-white border border-red-300 rounded-lg hover:bg-red-50 hover:border-red-400 transition-all duration-200 font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              {t('buttons.delete')}
            </button>
          )}
        </div>
      </div>

              <CategoryForm
        category={category}
        onSubmit={handleSubmit}
        onCancel={() => navigate({ to: '/categories' })}
        onDelete={handleDelete}
        isLoading={loading}
      />

      {!isCreateMode && category && (
        <ConfirmDialog
          isOpen={showDeleteDialog}
          onClose={() => setShowDeleteDialog(false)}
          onConfirm={handleDelete}
          title={t('categories.deleteConfirmTitle')}
          message={t('messages.deleteCategoryConfirm', { name: category.id })}
          confirmLabel={t('buttons.delete')}
          confirmVariant="danger"
          loading={deleting}
        />
      )}
    </div>
  )
}